import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api-client.js';
import type { FinancePeriod } from './use-finance-summary.js';

export interface RevenueComponent {
  component: string;
  amount: number;
  stops: number;
}

export interface ChargeDetail {
  charge_type?: string;
  description?: string;
  amount: number;
  date?: string;
}

export interface IspComparison {
  isp_number: string;
  terminal?: string;
  total_charges: number;
  chargebacks: number;
  net_settlement: number;
  charge_detail: ChargeDetail[];
}

export interface RevenueChargeback {
  isp_number?: string;
  charge_type?: string;
  description?: string;
  amount: number;
  date?: string;
}

export interface RevenueNonScan {
  isp_number?: string;
  date?: string;
  amount: number;
  stops: number;
}

export interface RevenueReportData {
  period: FinancePeriod;
  daily_by_isp: Record<string, string | number>[];
  components: RevenueComponent[];
  isp_comparison: IspComparison[];
  chargebacks: RevenueChargeback[];
  non_scans: RevenueNonScan[];
}

interface RevenueResponse extends RevenueReportData {
  error?: string;
}

export function useFinanceRevenue(periodType: string) {
  const [data, setData] = useState<RevenueReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (periodType) params.set('period', periodType);

      const resp = await apiFetch<RevenueResponse>(`/api/finance/revenue?${params.toString()}`);
      if (resp?.error) throw new Error(resp.error);

      setData(resp);
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err.message : 'Revenue report load failed');
    } finally {
      setLoading(false);
    }
  }, [periodType]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, refreshRevenue: load };
}
